import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { useRequireSuperAdmin } from '../../lib/useRequireSuperAdmin';
import { api, type SuperUser } from '../../lib/api';
import { relativeTime } from '../../lib/dashboardHelpers';

export default function AdminUsersPage() {
  const gate = useRequireSuperAdmin();
  const navigate = useNavigate();
  const [users, setUsers] = useState<SuperUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!gate.ready) return;
    api
      .superUsers()
      .then(setUsers)
      .catch(err => setError((err as Error).message || 'Failed to load users'));
  }, [gate.ready]);

  const filtered = useMemo(() => {
    if (!users) return null;
    if (!filter.trim()) return users;
    const q = filter.toLowerCase();
    return users.filter(
      u =>
        u.email.toLowerCase().includes(q) ||
        u.name.toLowerCase().includes(q) ||
        (u.workspace_domain ?? '').toLowerCase().includes(q),
    );
  }, [users, filter]);

  if (!gate.ready) return null;

  return (
    <AdminShell title="Users" subtitle="Every user across all workspaces.">
      {error && (
        <div
          style={{
            padding: 14,
            marginBottom: 16,
            background: 'var(--color-peach)',
            color: 'var(--color-peach-deep)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div
        className="bg-surface overflow-hidden"
        style={{
          border: '1px solid var(--color-hairline)',
          borderRadius: 16,
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div
          className="flex items-center bg-surface"
          style={{
            gap: 12,
            padding: '14px 20px',
            borderBottom: '1px solid var(--color-hairline)',
          }}
        >
          <input
            placeholder="Filter by email, name, or workspace…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="flex-1 outline-none text-[13px] text-ink min-w-0"
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-hairline)',
              borderRadius: 10,
              background: 'var(--color-bg-alt)',
            }}
          />
          <span className="text-xs text-ink-muted whitespace-nowrap">
            {filtered ? (
              <>
                <b className="font-semibold text-ink-soft">{filtered.length}</b> of{' '}
                {users?.length ?? 0}
              </>
            ) : (
              '…'
            )}
          </span>
        </div>

        <div
          className="grid bg-bg-alt"
          style={{
            gridTemplateColumns: '2fr 1.5fr 1.5fr 70px 70px 110px 110px',
            gap: 12,
            padding: '10px 20px',
            borderBottom: '1px solid var(--color-hairline)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--color-ink-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <div>Email</div>
          <div>Name</div>
          <div>Workspace</div>
          <div>Role</div>
          <div>Links</div>
          <div>Last seen</div>
          <div>Joined</div>
        </div>

        {!filtered ? (
          <div className="text-ink-soft text-sm" style={{ padding: 40, textAlign: 'center' }}>
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-ink-muted text-sm" style={{ padding: 40, textAlign: 'center' }}>
            No users match.
          </div>
        ) : (
          filtered.map(u => (
            <div
              key={u.id}
              onClick={() => navigate(`/admin/users/${u.id}`)}
              className="grid items-center cursor-pointer transition-colors duration-150"
              style={{
                gridTemplateColumns: '2fr 1.5fr 1.5fr 70px 70px 110px 110px',
                gap: 12,
                padding: '12px 20px',
                borderBottom: '1px solid var(--color-hairline)',
                background: 'var(--color-surface)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--color-bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--color-surface)';
              }}
            >
              <div className="text-sm text-ink font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                {u.email}
              </div>
              <div className="text-xs text-ink-soft overflow-hidden text-ellipsis whitespace-nowrap">
                {u.name}
              </div>
              <div className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap font-mono">
                {u.workspace_domain ?? '—'}
                {u.workspace_is_personal ? (
                  <span style={{ marginLeft: 6, fontStyle: 'italic' }}>(personal)</span>
                ) : null}
              </div>
              <div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 6,
                    background:
                      u.role === 'admin' ? 'var(--color-lemon)' : 'var(--color-bg-alt)',
                    color:
                      u.role === 'admin'
                        ? 'var(--color-lemon-deep)'
                        : 'var(--color-ink-soft)',
                  }}
                >
                  {u.role}
                </span>
              </div>
              <div className="text-sm tabular-nums">{u.link_count}</div>
              <div className="text-xs text-ink-muted">
                {u.extension_last_seen ? relativeTime(u.extension_last_seen) : '—'}
              </div>
              <div className="text-xs text-ink-muted">{relativeTime(u.created_at)}</div>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}
